<ng-template #noPermission>
    <p>Sorry you don't have permission to access this page.</p>
</ng-template>

<div *ngIf="hasAccess; else noPermission">
    <ng-container *ngIf="(loading$ | async) || isImageUploading">
        <div class="spinner-container">
            <div class="spinner">
                <mat-progress-spinner mode="indeterminate"></mat-progress-spinner>
            </div>
        </div>
    </ng-container>
    <div class="card">
        <div class="card-header">
            <h4>{{ isEditMode ? 'Edit Rule' : 'New Rule' }}</h4>
        </div>

        <div class="card-body" [formGroup]="ruleForm">
            <div class="status-message status-message-danger" *ngIf="errorMessage !== '' && errorMessage !== null">
                <span class="display-message"> {{errorMessage}} </span>
                <span class="close-btn" (click)="resetErrorMessage()"><mat-icon>cancel</mat-icon></span>
            </div>

            <div class="status-message status-message-success" *ngIf="successMessage !== '' && successMessage !== null">
                <span class="display-message"> {{successMessage}} </span>
                <span class="close-btn" (click)="resetSuccessMessage()"><mat-icon>cancel</mat-icon></span>
            </div>

            <!-- Rule Name -->
            <div class="fraud-form-field">
                <label>Rule Name<span class="required-indicator">*</span></label>
                <mat-form-field appearance="fill">
                    <input matInput formControlName="name" />
                    <mat-error *ngIf="name?.touched && name?.invalid"> Rule name is required </mat-error>
                </mat-form-field>
            </div>

            <!-- Rule Type Selection -->
            <div class="fraud-form-field rule-type">
                <label>Rule Type<span class="required-indicator">*</span></label>
                <div class="radio-group">
                    <div class="radio-option">
                        <input
                            type="radio"
                            id="singleTransaction"
                            formControlName="ruleType"
                            [value]="ruleTypes.SINGLE"
                            (change)="onRuleTypeChange()"
                        />
                        <label for="singleTransaction">Single Transaction</label>
                    </div>
                    <div class="radio-option">
                        <input
                            type="radio"
                            id="repeatedTransactions"
                            formControlName="ruleType"
                            [value]="ruleTypes.REPEATED"
                            (change)="onRuleTypeChange()"
                        />
                        <label for="repeatedTransactions">Repeated Transactions</label>
                    </div>
                </div>
            </div>

            <div
                class="fraud-form-field"
                *ngIf="ruleForm.get('ruleType')?.value === ruleTypes.SINGLE || ruleForm.get('ruleType')?.value === ruleTypes.REPEATED"
            >
                <label>Products<span class="required-indicator">*</span></label>

                <mat-form-field appearance="outline" class="full-width-select">
                    <mat-label>Products</mat-label>
                    <mat-select formControlName="productLineNames" multiple>
                        <!-- Custom trigger to display selected options -->
                        <mat-select-trigger>
                            <ng-container *ngIf="productLineNames.value?.length > 0">
                                <span class="selected-option">{{ productLineNames.value[0] }}</span>
                                <span *ngIf="productLineNames.value.length > 1" class="additional-count">
                                    ( +{{ productLineNames.value.length - 1 }} {{ productLineNames.value.length === 2 ?
                                    'other' : 'others' }} )
                                </span>
                            </ng-container>
                        </mat-select-trigger>

                        <!-- Action buttons container -->
                        <div class="select-actions-container">
                            <!-- Select All button -->
                            <button type="button" class="select-action-button select-all" (click)="selectAllProducts()">
                                Select All
                            </button>

                            <!-- Deselect All button -->
                            <button
                                type="button"
                                class="select-action-button deselect-all"
                                (click)="deselectAllProducts()"
                            >
                                Deselect All
                            </button>
                        </div>

                        <!-- Divider between action buttons and options -->
                        <mat-divider></mat-divider>

                        <!-- Regular product options -->
                        <ng-container *ngIf="ruleType?.value === ruleTypes.SINGLE">
                            <mat-option *ngFor="let product of availableProductsSingle" [value]="product">
                                {{ product }}
                            </mat-option>
                        </ng-container>
                        <ng-container *ngIf="ruleType?.value === ruleTypes.REPEATED">
                            <mat-option *ngFor="let product of availableProductsRepeated" [value]="product">
                                {{ product }}
                            </mat-option>
                        </ng-container>
                    </mat-select>
                    <mat-error> Please select at least one product </mat-error>
                </mat-form-field>
            </div>

            <!-- Single Transaction Fields -->
            <div
                *ngIf="ruleForm.get('ruleType')?.value === ruleTypes.SINGLE"
                class="fraud-form-field single-transaction"
            >
                <label style="display: flex"
                    >Threshold Value (CHF)<span class="required-indicator">*</span>
                    <mat-icon [matTooltip]="THRESHOLD_VALUE_TOOLTIP_TEXT" matTooltipPosition="above" class="info-icon"
                        >info</mat-icon
                    >
                </label>
                <input type="number" formControlName="thresholdValue" min="0" />
                <div
                    class="error-container"
                    *ngIf="ruleForm.get('thresholdValue')?.touched && ruleForm.get('thresholdValue')?.invalid"
                >
                    <div class="error-message">
                        Threshold value is required and must be a whole number greater than or equal to 0
                    </div>
                </div>
            </div>

            <!-- Repeated Transactions Fields -->
            <div *ngIf="ruleForm.get('ruleType')?.value === ruleTypes.REPEATED" class="repeated-transactions">
                <div class="fraud-form-field">
                    <label style="display: flex"
                        >Number of Repeated Transactions<span class="required-indicator">*</span>
                        <mat-icon
                            [matTooltip]="REPEATED_TRANSACTIONS_TOOLTIP_TEXT"
                            matTooltipPosition="above"
                            class="info-icon"
                            >info</mat-icon
                        >
                    </label>
                    <input type="number" formControlName="numberOfTransactions" min="1" />
                </div>

                <div class="fraud-form-field">
                    <label>Within<span class="required-indicator">*</span></label>
                    <input type="number" formControlName="timeframeInMinutes" min="1" />
                    <label style="margin-left: 10px">Minutes</label>
                </div>
            </div>

            <!-- Error messages for repeated transactions -->
            <div *ngIf="ruleForm.get('ruleType')?.value === ruleTypes.REPEATED" class="error-container">
                <div
                    class="error-message"
                    *ngIf="ruleForm.get('numberOfTransactions')?.touched && ruleForm.get('numberOfTransactions')?.invalid"
                >
                    Number of transactions is required and must be a whole number greater than or equal to 1
                </div>
                <div
                    class="error-message"
                    *ngIf="ruleForm.get('timeframeInMinutes')?.touched && ruleForm.get('timeframeInMinutes')?.invalid"
                >
                    Timeframe is required and must be a whole number greater than or equal to 1
                </div>
            </div>

            <mat-error *ngIf="isNoProductsAvailable" class="select-product-error-message">
                All products are included in other fraud pop-up rules. To create a new rule, please remove the desired
                product from the existing rules.
            </mat-error>

            <hr />

            <!-- Pop-Up Message -->
            <div class="fraud-form-field">
                <label>Pop-Up message<span class="required-indicator">*</span></label>

                <div class="language-tabs">
                    <div class="language-tab">
                        <div class="language-inputs">
                            <div class="mx-3" *ngFor="let language of LANGUAGES">
                                <div class="desc-div" [class.invalid-field]="isMessageInvalid(language.key)">
                                    <label>
                                        {{ language.label }}
                                        <span class="required-indicator">*</span>
                                    </label>
                                    <div (click)="addText(language.key)">
                                        <div
                                            class="textarea-field text-ellipsis"
                                            [innerHtml]="ruleForm?.get('message')?.get(language.key)?.value | markdownToSafeHtml"
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="error-message-container" *ngIf="hasAnyMessageError()">
                            <div class="error-message">All message languages are required</div>
                        </div>
                    </div>
                </div>
            </div>

            <hr />

            <!-- Pop-Up Image -->
            <div class="fraud-form-field">
                <label
                    >Pop-Up image
                    <mat-icon [matTooltip]="IMAGE_TOOLTIP_TEXT" matTooltipPosition="above" class="info-icon"
                        >info</mat-icon
                    ></label
                >
                <div class="image-upload">
                    <div class="upload-buttons">
                        <button
                            type="button"
                            class="btn-submit"
                            (click)="fileInput.click()"
                            [disabled]="imageUrl?.value != null"
                        >
                            Choose file
                        </button>
                        <button
                            [disabled]="imageUrl?.value == null"
                            type="button"
                            class="btn-cancel"
                            (click)="removeImage()"
                        >
                            Cancel
                        </button>
                        <input
                            #fileInput
                            type="file"
                            accept="image/*"
                            style="display: none"
                            (change)="onFileSelected($event)"
                        />
                    </div>

                    <div class="image-preview" *ngIf="imageUrl?.value">
                        <img [src]="imageUrl?.value" alt="Preview" />
                    </div>
                </div>

                <div class="error-message-container" *ngIf="imageErrorMessage !== null">
                    <div class="error-message">{{ imageErrorMessage }}</div>
                </div>
            </div>
        </div>

        <div class="card-footer">
            <button type="button" class="btn-cancel" (click)="cancel()">Cancel</button>
            <button
                type="button"
                class="btn-submit"
                (click)="validateBeforeSaveRule()"
                [disabled]="ruleForm.invalid || isNoProductsAvailable"
            >
                Save
            </button>
            <button
                *ngIf="!isEditMode"
                type="button"
                class="btn-submit"
                (click)="validateBeforeSaveRule(false)"
                [disabled]="ruleForm.invalid || isNoProductsAvailable"
            >
                Save and add new
            </button>
        </div>
    </div>
</div>
